import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Autoplay from "embla-carousel-autoplay";
import { classNames, hexToRgba } from "../helpers";
import { FocusedComponentType, OverRides } from "../popup/types";
import { adjustPixelValue, isMobilePreviewMode } from "../popup/utils";
import {
  Carousel as ShadcnCarousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
  type CarouselOptions,
} from "../@shadcn/components/ui/carousel";
import { ChevronLeft } from "react-feather";

type ButtonPropType = ComponentPropsWithRef<"button"> & {
  styles?: {
    backgroundColor?: string;
  };
  customizeUI?: {
    iconRequired?: boolean;
    isMobilePreview?: boolean;
  };
  direction: "prev" | "next";
};

const CarouselButton: React.FC<ButtonPropType> = (props) => {
  const { children, styles, direction, ...restProps } = props;
  const bgColor = styles?.backgroundColor || "#000000";

  let solid = hexToRgba(bgColor, 1);
  let transparent = hexToRgba(bgColor, 0);

  // is a named color
  if (!bgColor.includes("#")) {
    solid = bgColor;
    transparent = "#00000000";
  }

  const isNext = direction === "next";
  const gradientDirection = isNext ? "to left" : "to right";
  const marginClass = isNext ? "ui-ml-6" : "ui-mr-6";

  return (
    <button
      className="ui-w-14 ui-h-full ui-flex ui-items-center ui-justify-center"
      type="button"
      {...restProps}
    >
      {props.customizeUI?.iconRequired && (
        <div
          className={classNames(
            "ui-p-2 ui-rounded-full ui-bg-gray-light ui-mb-4",
            props.customizeUI?.isMobilePreview
              ? `ui-scale-50 ${marginClass}`
              : "",
            isNext ? "ui-rotate-180" : ""
          )}
        >
          <ChevronLeft />
        </div>
      )}

      {children}
    </button>
  );
};

const PrevButton: React.FC<Omit<ButtonPropType, "direction">> = (props) => {
  return <CarouselButton {...props} direction="prev" />;
};

const NextButton: React.FC<Omit<ButtonPropType, "direction">> = (props) => {
  return <CarouselButton {...props} direction="next" />;
};

type PropType = {
  slides: React.ReactNode[];
  options?: CarouselOptions;
  styles?: {
    button?: {
      backgroundColor?: string;
    };
    card?: {
      borderRadius?: string;
    };
  };
  overRides?: OverRides;
  onSelectedIndexChange?: (index: number) => void;
};

export const BannerCarousel: React.FC<PropType> = (props) => {
  const slidesRef = useRef<HTMLDivElement[]>([]);
  const { slides, options, styles, overRides, onSelectedIndexChange } = props;
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Create autoplay plugin instance with ref to maintain same instance
  const autoplayPlugin = React.useRef(
    Autoplay({
      playOnInit: overRides?.isPreview ? false : true,
      delay: 5000,
      stopOnInteraction: true,
      stopOnFocusIn: true,
      stopOnMouseEnter: true,
    })
  );

  // Autoplay control functions using shadcn's carousel API
  const stopAutoPlay = useCallback(() => {
    const plugins = api?.plugins() as any;
    const autoplay = plugins?.autoplay;
    if (!autoplay) return;
    autoplay.stop();
  }, [api]);

  const resumeAutoPlay = useCallback(() => {
    const plugins = api?.plugins() as any;
    const autoplay = plugins?.autoplay;
    if (!autoplay) return;
    autoplay.play();
  }, [api]);

  // Update scroll button states
  useEffect(() => {
    if (!api) return;

    const updateScrollButtons = () => {
      const currentIndex = api.selectedScrollSnap();
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setSelectedIndex(currentIndex);
      onSelectedIndexChange?.(currentIndex);
    };

    updateScrollButtons();
    api.on("select", updateScrollButtons);
    api.on("reInit", updateScrollButtons);

    return () => {
      api.off("select", updateScrollButtons);
      api.off("reInit", updateScrollButtons);
    };
  }, [api, onSelectedIndexChange]);

  useEffect(() => {
    if (
      Boolean(overRides) &&
      api &&
      overRides?.focusedComponent?.type === FocusedComponentType.CARD &&
      (overRides?.focusedComponent?.cardIndex ?? -1) >= 0
    ) {
      api.scrollTo(overRides?.focusedComponent?.cardIndex ?? 0);
      slidesRef.current.forEach((slide, index) => {
        if (index === overRides?.focusedComponent?.cardIndex) {
          slide?.classList.remove("ui-border-transparent");
          slide?.classList.add(
            "ui-border-primary-dark",
            "ui-shadow-lg",
            "ui-rounded-[inherit]"
          );
          if (slide)
            slide.style.borderRadius = adjustPixelValue(
              styles?.card?.borderRadius || "0px",
              2
            );
        } else {
          slide?.classList.remove("ui-border-primary-dark", "ui-shadow-lg");
          slide?.classList.add("ui-border-transparent");
          if (slide) slide.style.borderRadius = "0px";
        }
      });
    } else {
      slidesRef.current.forEach((slide) => {
        slide?.classList.remove("ui-border-primary-dark", "ui-shadow-lg");
        slide?.classList.add("ui-border-transparent");
        if (slide) slide.style.borderRadius = "0px";
      });
    }

    if (
      overRides?.focusedComponent?.type === FocusedComponentType.CARDS &&
      Boolean(overRides?.focusedComponent.cardStyleType) &&
      api
    ) {
      const activeIndex = api?.selectedScrollSnap();
      slidesRef.current.forEach((slide, index) => {
        if (index === activeIndex) {
          slide?.classList.remove("ui-border-transparent");
          slide?.classList.add(
            "ui-border-primary-dark",
            "ui-shadow-lg",
            "ui-rounded-[inherit]"
          );
          if (slide)
            slide.style.borderRadius = adjustPixelValue(
              styles?.card?.borderRadius || "0px",
              2
            );
        } else {
          slide?.classList.remove("ui-border-primary-dark", "ui-shadow-lg");
          slide?.classList.add("ui-border-transparent");
          if (slide) slide.style.borderRadius = "0px";
        }
      });
    }
  }, [overRides?.focusedComponent, api, styles?.card?.borderRadius]);

  const handleRef = (index: number, el?: HTMLDivElement | null) => {
    if (el) {
      slidesRef.current[index] = el;
    }
  };

  const handlePrevClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    api?.scrollPrev();
  };

  const handleNextClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    api?.scrollNext();
  };

  if (!slides.length) return null;

  const shouldShowBottomNavigation = slides.length > 1;
  const slideAlignment = "center";
  const contentSpacingClass = overRides?.isPreview ? "!-ui-ml-3" : "!-ui-ml-4";
  const itemSpacingClass = overRides?.isPreview ? "!ui-pl-3" : "!ui-pl-4";

  return (
    <ShadcnCarousel
      opts={{ ...options, align: slideAlignment, containScroll: "trimSnaps" }}
      plugins={[autoplayPlugin.current]}
      setApi={setApi}
      className="ui-relative ui-w-full"
      onMouseEnter={() => {
        if (slides.length > 1) {
          stopAutoPlay();
        }
      }}
      onMouseLeave={() => {
        if (slides.length > 1 && !overRides?.isPreview) {
          resumeAutoPlay();
        }
      }}
      onClick={() => {
        if (overRides?.isPreview) {
          const currentIndex = api?.selectedScrollSnap() ?? 0;
          overRides?.setScrollToCard?.(currentIndex);
        }
      }}
    >
      <CarouselContent
        className={classNames(
          "ui-touch-pan-y ui-py-2 md:ui-py-4",
          contentSpacingClass,
          slides.length === 1 ? "ui-justify-center" : ""
        )}
      >
        {slides.map((slide, index) => (
          <CarouselItem
            key={index}
            className={classNames(
              "ui-transform ui-translate-x-0 ui-translate-y-0 ui-translate-z-0",
              itemSpacingClass,
              isMobilePreviewMode(overRides)
                ? "!ui-basis-[70%]"
                : "!ui-basis-[82%]"
            )}
          >
            <div
              className={classNames(
                "ui-transition-transform ui-duration-300",
                overRides?.isPreview ? "ui-border-2 ui-border-transparent ui-rounded-[inherit]" : ""
              )}
              ref={(el) => handleRef(index, el)}
            >
              {slide}
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      {shouldShowBottomNavigation && (
        <div className={classNames(
          "ui-items-center ui-justify-center ui-gap-3 ui-pb-3 md:ui-pb-5 ui-pt-2 md:ui-pt-3",
          isMobilePreviewMode(overRides) ? "ui-flex" : "ui-hidden md:ui-flex"
        )}>
          <button
            type="button"
            onClick={handlePrevClick}
            className="ui-flex ui-size-5 ui-items-center ui-justify-center ui-rounded-full disabled:ui-opacity-40"
            disabled={!canScrollPrev}
            aria-label="Previous slide"
          >
            <ChevronLeft size={14} className="ui-text-coal-light" />
          </button>
          <div className="ui-flex ui-items-center ui-gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  api?.scrollTo(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
                className={classNames(
                  "ui-h-1.5 ui-w-1.5 ui-rounded-full ui-transition-all",
                  selectedIndex === index
                    ? "ui-bg-primary-dark ui-w-3 ui-h-1"
                    : "ui-bg-[#003a5d]/20"
                )}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={handleNextClick}
            className="ui-flex ui-h-5 ui-w-5 ui-items-center ui-justify-center ui-rounded-full disabled:ui-opacity-40"
            disabled={!canScrollNext}
            aria-label="Next slide"
          >
            <ChevronLeft
              size={14}
              className="ui-rotate-180 ui-text-coal-light"
            />
          </button>
        </div>
      )}

    </ShadcnCarousel>
  );
};
